import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, 
  ChevronDown, 
  Clock, 
  Briefcase, 
  Calendar, 
  Users, 
  RefreshCw,
  Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDropdown({ user }) {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    fetchCounts();
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Get pending jobs count
      const pendingRes = await axios.get(`${API}/admin/jobs/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCount(pendingRes.data.length);

      // Get total users count
      const usersRes = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotalUsers(usersRes.data.length);
    } catch (error) {
      console.error("Failed to fetch admin counts", error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2 relative"
          data-testid="admin-dropdown-trigger"
        >
          <Shield className="h-4 w-4" />
          <span className="hidden md:inline">Admin Panel</span>
          {pendingCount > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 bg-red-500 text-white h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
              data-testid="pending-badge"
            >
              {pendingCount}
            </Badge>
          )}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Admin Control Panel
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem 
          onClick={() => navigate("/admin?tab=pending")}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="admin-dropdown-pending"
        >
          <Clock className="h-4 w-4 text-yellow-600" />
          <span>Pending Approvals</span>
          {pendingCount > 0 && (
            <Badge className="ml-auto bg-yellow-100 text-yellow-700">
              {pendingCount}
            </Badge>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem 
          onClick={() => navigate("/admin?tab=users")}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="admin-dropdown-users"
        >
          <Users className="h-4 w-4 text-blue-600" />
          <span>Manage Users</span>
          <Badge variant="outline" className="ml-auto">
            {totalUsers}
          </Badge>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem 
          onClick={() => navigate("/admin?tab=jobs")}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="admin-dropdown-jobs"
        >
          <Briefcase className="h-4 w-4 text-green-600" />
          <span>Manage Jobs</span>
        </DropdownMenuItem>

        <DropdownMenuItem 
          onClick={() => navigate("/admin?tab=events")}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="admin-dropdown-events"
        >
          <Calendar className="h-4 w-4 text-purple-600" />
          <span>Manage Events</span>
        </DropdownMenuItem>

        <DropdownMenuItem 
          onClick={() => navigate("/admin?tab=codes")}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="admin-dropdown-codes"
        >
          <Shield className="h-4 w-4 text-orange-600" />
          <span>Verification Codes</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem 
          onClick={() => {
            navigate("/admin?tab=jobs&action=sync");
            // Trigger sync after navigation
            setTimeout(() => {
              const syncButton = document.querySelector('[data-testid="sync-jobs-button"]');
              if (syncButton) syncButton.click();
            }, 500);
          }}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="admin-dropdown-sync"
        >
          <RefreshCw className="h-4 w-4 text-teal-600" />
          <span>Sync External Jobs</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem 
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 cursor-pointer font-medium"
          data-testid="admin-dropdown-dashboard"
        >
          <Settings className="h-4 w-4" style={{ color: '#FF6B6B' }} />
          <span>Full Dashboard</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
